import { LoadingSpinner } from '@web_opxp/components';
import { ObFlowService, SentryService, isLocalhost, isProduction } from '@web_opxp/features';
import { LanguageService } from '@web_opxp/language';
import { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { Route, RouterProvider, Routes, createBrowserRouter } from 'react-router';
import { HistoryService } from 'web_core_library';
import * as ENV from './base/env';
import { AssetsBaseUrl, BasePath, BuildID } from './base/env';
import initHreflangService from './base/hreflangService';
import NotAuthorizedPage from './error/pages/NotAuthorizedPage';
import NotFoundPage from './error/pages/NotFoundPage';
import GalleryApp from './gallery/GalleryApp';
import './index.scss';
import WelcomePage from './prereg/WelcomePage';
import reportWebVitals from './reportWebVitals';
import * as AppRoutes from './routes';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

if (!isLocalhost) {
  SentryService.initSentry(isProduction(), BuildID);
}
if (!isProduction()) {
  SentryService.enableReplay();
}

const OnboardingApp = lazy(() => import('./RootApp'));
const ExercisesBigLandingPage = lazy(() => import('./landing/exercisesBig'));
const ExercisesDeviceLandingPage = lazy(() => import('./landing/exercisesDevice'));
const QuestionnairePage = lazy(() => import('./questionnaire'));
const NativeLandingPage = lazy(() => import('./landing/native'));
const TestimonialLandingPage = lazy(() => import('./landing/testimonial'));
const GoLandingPage = lazy(() => import('./landing/lumosity'));
const QuizPage = lazy(() => import('./quiz'));
const CorpLandingPage = lazy(() => import('./landing/corp'));
const BlogGoLandingPage = lazy(() => import('./landing/blogGo'));
const AccountDeletedPage = lazy(() => import('./landing/accountDeleted'));
const AssessmentPage = lazy(() => import('./assessment/AssessmentPage'));
const EvaluationPage = lazy(() => import('./evaluation/EvaluationPage'));
const PrescriptionServicePage = lazy(() => import('./med/prescriptionService'));
const ReprescriptionServicePage = lazy(() => import('./med/newReprescription'));
const ReprescriptionServicePagev2 = lazy(() => import('./med/represcription'));
const InstantPrescriptionServicePage = lazy(() => import('./med/instantPrescription'));
const NewPrescriptionServicePage = lazy(() => import('./med/newPrescriptionService'));
const LegalsPage = lazy(() => import('./legals'));

const container = document.getElementById('root');
// eslint-disable-next-line
const root = createRoot(container!);

export const router = createBrowserRouter(
  [
    {
      path: '/*',
      element: (
        <Suspense fallback={<LoadingSpinner assetsUrl={AssetsBaseUrl} />}>
          <Routes>
            <Route path={AppRoutes.accountDeletedRoute} Component={AccountDeletedPage} />
            <Route path={AppRoutes.exercisesBigRoute} Component={ExercisesBigLandingPage} />
            <Route
              path={AppRoutes.exercisesBigFrontpageRoute}
              Component={() => <ExercisesBigLandingPage frontpage={true} />}
            />
            <Route
              path={AppRoutes.exercisesBigWhiteRoute}
              Component={() => <ExercisesBigLandingPage color="white" />}
            />
            <Route
              path={AppRoutes.exercisesBigFrontpageWhiteRoute}
              Component={() => <ExercisesBigLandingPage color="white" frontpage={true} />}
            />
            <Route path={AppRoutes.exercisesDeviceRoute} Component={ExercisesDeviceLandingPage} />
            <Route
              path={AppRoutes.exercisesDeviceFrontpageRoute}
              Component={() => <ExercisesDeviceLandingPage frontpage={true} />}
            />
            <Route
              path={AppRoutes.exercisesDeviceWhiteRoute}
              Component={() => <ExercisesDeviceLandingPage color="white" />}
            />
            <Route
              path={AppRoutes.exercisesDeviceFrontpageWhiteRoute}
              Component={() => <ExercisesDeviceLandingPage color="white" frontpage={true} />}
            />
            <Route path={AppRoutes.questionnaireRoute} Component={QuestionnairePage} />
            <Route path={AppRoutes.nativeRoute} Component={NativeLandingPage} />
            <Route path={AppRoutes.testimonialRoute} Component={TestimonialLandingPage} />
            <Route
              path={AppRoutes.testimonialOnboardingCTARoute}
              Component={() => <TestimonialLandingPage onboardingCTA />}
            />
            <Route path={AppRoutes.goRoute} Component={GoLandingPage} />
            <Route path={AppRoutes.corpRoute} Component={CorpLandingPage} />
            <Route path={AppRoutes.quizRoute} Component={QuizPage} />
            <Route path={AppRoutes.blogGoRoute} Component={BlogGoLandingPage} />
            <Route path={AppRoutes.prescriptionServiceRoute} Component={PrescriptionServicePage} />
            <Route path={AppRoutes.medPrescriptionServiceRoute} Component={PrescriptionServicePage} />
            <Route path={AppRoutes.medNewPrescriptionServiceRoute} Component={NewPrescriptionServicePage} />
            <Route path={AppRoutes.medReprescriptionServiceRoute} Component={ReprescriptionServicePage} />
            <Route path={AppRoutes.medReprescriptionServiceRoutev2} Component={ReprescriptionServicePagev2} />
            <Route path={AppRoutes.medInstantPrescriptionServiceRoute} Component={InstantPrescriptionServicePage} />
            <Route path={AppRoutes.legalsRoute} Component={LegalsPage} />
            <Route path={AppRoutes.galleryRoute} Component={GalleryApp} />
            <Route path="/*" Component={OnboardingApp}>
              <Route element={<WelcomePage />} index />
              <Route path={AppRoutes.assessmentRoute} element={<AssessmentPage />} index />
              <Route path={AppRoutes.evaluationRoute} element={<EvaluationPage />} index />
              <Route path={AppRoutes.welcomeRoute} element={<WelcomePage />} index />
              <Route path={AppRoutes.notAuthorizedRoute} Component={NotAuthorizedPage} />
              <Route path="*" Component={NotFoundPage} />
            </Route>
          </Routes>
        </Suspense>
      ),
    },
  ],
  { basename: BasePath }
);

HistoryService.initialize(router, {
  loginBaseUrl: ENV.LoginBaseUrl,
  logoutBaseUrl: ENV.LogoutBaseUrl,
  BlogBaseUrl: ENV.BlogBaseUrl,
  getLanguageFunction: () => LanguageService.getLanguage() as string,
  OnboardingAppPath: '/',
  SocialAppPath: ENV.SocialAppPath,
  SubscribeUrl: ENV.SubscribeBaseUrl,
  TrainingBaseUrl: ENV.TrainingBaseUrl,
  signupBaseUrl: ENV.RegistrationBaseUrl,
});

const currentSearchParams = HistoryService.getCurrentSearchParams();

ObFlowService.init('onboarding', currentSearchParams, ENV.AssetsBaseUrl, {
  getExternalUrl: HistoryService.getExternalUrl,
  getInternalUrl: HistoryService.getInternalUrl,
  getRegistrationUrl: HistoryService.getSignupUrl,
  redirectExternal: HistoryService.redirectExternal,
  redirectInternal: HistoryService.redirectInternal,
  redirectRegistration: HistoryService.redirectSignup,
  urlMap: {
    training: ENV.TrainingBaseUrl,
    sso: ENV.AuthBaseUrl,
    subscribe: ENV.SubscribeBaseUrl,
    user: ENV.UserAppPath,
  },
});

root.render(<RouterProvider router={router} />);

// init listener for hreflang tags udpate
initHreflangService();
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorkerRegistration.register();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
